
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Button } from "@/components/ui/button"
import { useAuth } from "@/context/AuthContext"
import { useNavigate } from "react-router-dom"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { Settings, User, LogOut, MessageSquareText, Bell, HelpCircle } from "lucide-react"
import { cn } from "@/lib/utils"
import { useUnreadMessages } from "@/hooks/useUnreadMessages"
import { useNotificationsUnified } from "@/hooks/useNotificationsUnified"
import { useOnboardingContext } from "@/context/OnboardingContext"

interface ProfileDropdownProps {
  className?: string
}

const ProfileDropdown: React.FC<ProfileDropdownProps> = ({ className }) => {
  const { user, profile, signOut } = useAuth()
  const { count: unreadCount, isLoading: isLoadingMessages } = useUnreadMessages()
  const { unreadCount: notificationCount } = useNotificationsUnified({ limit: 5 })
  const { handleOpen } = useOnboardingContext()
  const navigate = useNavigate()

  const handleLogout = () => {
    if (signOut) {
      signOut()
    }
  }

  const handleRestartOnboarding = () => {
    handleOpen()
  }
  
  // Navigate to the appropriate profile page based on user role
  const handleProfileClick = () => {
    if (!profile) {
      navigate('/profile')
      return
    }
    
    switch (profile.role) {
      case 'admin':
        navigate('/profile-admin')
        break
      case 'manager':
        navigate('/profile-manager')
        break

      default:
        navigate('/profile')
        break
    }
  }

  // Get user initials for avatar
  const getUserInitials = () => {
    if (profile?.fullName) {
      const names = profile.fullName.split(' ').filter(Boolean);
      if (names.length >= 2) {
        return `${names[0][0]}${names[names.length - 1][0]}`;
      } else if (names.length === 1) {
        return names[0][0];
      }
    }
    if (user?.email) {
      const email = user.email;
      const username = email.split('@')[0];
      return username.slice(0, 2).toUpperCase();
    }
    return 'A';
  };

  // Get display name with truncated last name
  const getDisplayName = () => {
    if (profile?.fullName) {
      const names = profile.fullName.split(' ').filter(Boolean);
      if (names.length >= 2) {
        // Имя + первые буквы фамилии
        const lastName = names[names.length - 1];
        const truncatedLastName = lastName.length > 3 ? `${lastName.slice(0, 3)}...` : lastName;
        return `${names[0]} ${truncatedLastName}`;
      }
      return names[0]; // Только имя
    }
    if (user?.email) {
      const username = user.email.split('@')[0];
      return username.charAt(0).toUpperCase() + username.slice(1);
    }
    return 'Пользователь';
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="outline"
          className={cn(
            "h-10 px-4 w-auto min-w-[116px] flex items-center justify-start gap-2", 
            className
          )}
        >          
          <Avatar className="h-8 w-8">
            <AvatarFallback className="text-xs bg-white text-gray-600">
              {getUserInitials()}
            </AvatarFallback>
          </Avatar>
          <span className="text-sm font-medium truncate">{getDisplayName()}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56 bg-white z-50">
        <DropdownMenuGroup>
          <DropdownMenuItem onClick={() => navigate('/notifications')}>
            <Bell className="mr-2 h-4 w-4" />
            <span>Уведомления</span>
            {notificationCount > 0 && (
              <span className="ml-auto bg-blue-500 text-white text-xs rounded-full px-2 py-0.5 min-w-[20px] text-center">
                {notificationCount > 99 ? '99+' : notificationCount}
              </span>
            )}
          </DropdownMenuItem>
          
          <DropdownMenuItem onClick={() => navigate('/request-center')}>
            <MessageSquareText className="mr-2 h-4 w-4" />
            <span>Центр Заявок</span>
            {!isLoadingMessages && unreadCount > 0 && (
              <span className="ml-auto bg-red-500 text-white text-xs rounded-full px-2 py-0.5 min-w-[20px] text-center">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </DropdownMenuItem>
          
          <DropdownMenuItem onClick={handleProfileClick}>
            <User className="mr-2 h-4 w-4" />
            <span>Профиль</span>
          </DropdownMenuItem>
          
          <DropdownMenuItem onClick={() => navigate('/settings')} data-onboard="nav-settings">
            <Settings className="mr-2 h-4 w-4" />
            <span>Настройки</span>
          </DropdownMenuItem>
        </DropdownMenuGroup>
        
        <DropdownMenuSeparator />
        
        {/* Кнопка повторного запуска onboarding */}
        <DropdownMenuItem 
          onClick={handleRestartOnboarding}
          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 focus:bg-blue-50"
        >
          <HelpCircle className="mr-2 h-4 w-4" />
          <span className="text-sm">Инструкция по настройке</span>
        </DropdownMenuItem>
        
        <DropdownMenuSeparator />
        
        <DropdownMenuItem onClick={handleLogout}>
          <LogOut className="mr-2 h-4 w-4" />
          Выйти
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export default ProfileDropdown
